import { createSkyImpactArt } from './140-sky-impact-art.mjs';
import { createSkyScorePopups } from './141-sky-score-popups.mjs';
import { createSkyDragonFire } from './138-sky-dragon-fire.mjs';
import { SKY_WORLD_ASSETS } from '../../../packages/domain/src/86-sky-world-scale.mjs';
import { createSkyProjectileArt } from './129-sky-projectile-art.mjs';
import { skyGuardianWake } from '../../../packages/domain/src/76-sky-campaign.mjs';
import {
  skyEffectiveEquipment,
  skyCannonInterval,
} from '../../../packages/domain/src/82-sky-bonuses.mjs';
import { skyRewardActive } from '../../../packages/domain/src/107-sky-rewards.mjs';
import { skyFlightCapabilities } from '../../../packages/domain/src/106-sky-capabilities.mjs';

/** Finite arcade effects driven only by domain time. Geometry/materials are shared;
 * the caller removes expired projectile groups and disposes this factory once.
 * Rider coordinates use feet at zero and forward -Z, independent of GLB orientation. */
export function createSkyEffects(T, scene, { reduced = () => false } = {}) {
  const owned = new Set();
  let disposed = false;
  const own = (resource) => {
    owned.add(resource);
    return resource;
  };
  const sphere = own(new T.IcosahedronGeometry(1, 1));
  const ring = own(new T.TorusGeometry(1, 0.025, 5, 40));
  const plumeGeometry = own(new T.CylinderGeometry(0.115, 0.015, 1.15, 10, 5, true));
  plumeGeometry.translate(0, -0.575, 0);
  const additive = (color, opacity = 1) =>
    own(
      new T.MeshBasicMaterial({
        color,
        transparent: true,
        opacity,
        blending: T.AdditiveBlending,
        depthWrite: false,
        toneMapped: false,
      }),
    );
  const projectileArt = createSkyProjectileArt(T, own, additive, reduced);
  const dragonFire = createSkyDragonFire(T, scene, own, reduced);
  const weaponCores = projectileArt.cores;
  const ice = additive('#d9fffa', 0.9);
  const supercharged = additive('#ffdf83', 0.95);
  const impacts = createSkyImpactArt(T, scene, own, reduced);
  const scorePopups = createSkyScorePopups(T, scene, own, reduced);
  const plumeMaterial = own(
    new T.ShaderMaterial({
      uniforms: { time: { value: 0 }, moving: { value: 1 }, charge: { value: 0 } },
      transparent: true,
      depthWrite: false,
      blending: T.AdditiveBlending,
      side: T.DoubleSide,
      toneMapped: false,
      vertexShader: `
        varying vec2 vUv;
        varying float viewDepth;
        uniform float time;
        uniform float moving;
        void main() {
          vUv = uv;
          vec3 p = position;
          float flow = sin(uv.y * 22.0 + time * 28.0) * 0.025 * moving;
          p.xz *= 1.0 + flow * (1.0 - uv.y);
          vec4 view = modelViewMatrix * vec4(p, 1.0);
          viewDepth = -view.z;
          gl_Position = projectionMatrix * view;
        }`,
      fragmentShader: `
        varying vec2 vUv;
        varying float viewDepth;
        uniform float time;
        uniform float moving;
        uniform float charge;
        void main() {
          float endFade = smoothstep(0.0, 0.24, vUv.y);
          float bands = 0.88 + 0.12 * sin(vUv.y * 32.0 - time * 32.0) * moving;
          vec3 color = mix(vec3(0.025, 0.22, 0.75), vec3(0.4, 1.25, 2.2), pow(vUv.y, 2.0));
          color = mix(color, mix(vec3(0.8, 0.22, 0.025), vec3(2.2, 1.6, 0.5), pow(vUv.y, 2.0)), charge);
          float lensFade = smoothstep(0.75, 2.2, viewDepth);
          gl_FragColor = vec4(color, endFade * bands * 0.85 * lensFade);
        }`,
    }),
  );
  const playerEffects = new T.Group();
  playerEffects.name = 'Sky Rider propulsion';
  scene.add(playerEffects);
  const plumes = new T.Group();
  playerEffects.add(plumes);
  for (const side of [-1, 1]) {
    const plume = new T.Mesh(plumeGeometry, plumeMaterial);
    plume.position.set(side * 0.27, 1.48, 0.31);
    plume.rotation.x = -0.16;
    plumes.add(plume);
  }
  const muzzle = new T.Mesh(sphere, ice);
  muzzle.position.set(-0.62, 1.55, -0.85);
  playerEffects.add(muzzle);
  const dualMuzzle = new T.Mesh(sphere, ice);
  dualMuzzle.visible = false;
  playerEffects.add(dualMuzzle);
  const shield = new T.Mesh(
    own(new T.IcosahedronGeometry(1, 2)),
    own(
      new T.MeshBasicMaterial({
        color: '#73dfff',
        wireframe: true,
        transparent: true,
        opacity: 0,
        depthWrite: false,
        toneMapped: false,
      }),
    ),
  );
  shield.name = 'Sky shield absorption';
  shield.position.y = 1;
  shield.scale.set(0.85, 1.4, 0.7);
  playerEffects.add(shield);
  const wake = new T.Mesh(ring, additive('#ffb657', 0.6));
  wake.name = 'Guardian fly-by pressure wake';
  scene.add(wake);
  const launchTracer = new T.Mesh(own(new T.CylinderGeometry(0.018, 0.012, 1, 6)), ice);
  launchTracer.name = 'Sky barrel to ballistic tracer';
  const dualTracer = launchTracer.clone();
  dualTracer.name = 'Sky secondary barrel to ballistic tracer';
  scene.add(launchTracer, dualTracer);
  const impactLight = new T.PointLight('#ff9c48', 0, 14, 2);
  impactLight.name = 'Sky local explosion light';
  scene.add(impactLight);
  const beamUp = new T.Vector3(0, 1, 0),
    beamDirection = new T.Vector3();
  const nova = new T.Group();
  nova.name = 'Sky Rider nova';
  scene.add(nova);
  const novaRing = own(new T.TorusGeometry(1, 0.009, 5, 64));
  for (let index = 0; index < 3; index++) {
    const wave = new T.Mesh(novaRing, additive(index === 2 ? '#ffd38c' : '#a4edff', 0.5));
    wave.rotation.x = index * 0.4;
    wave.scale.setScalar(index ? 0.92 : 1);
    nova.add(wave);
  }
  // One reusable batch covers all current explosions. No per-burst geometry,
  // materials, timers or random simulation; active age determines every particle.
  const particleCapacity = 768;
  const pixels = new Uint8Array(32 * 32 * 4);
  for (let y = 0; y < 32; y++)
    for (let x = 0; x < 32; x++) {
      const offset = (y * 32 + x) * 4;
      const radius = Math.hypot((x - 15.5) / 15.5, (y - 15.5) / 15.5);
      pixels[offset] = pixels[offset + 1] = pixels[offset + 2] = 255;
      pixels[offset + 3] = Math.round(Math.max(0, 1 - radius) ** 2 * 255);
    }
  const glowTexture = own(new T.DataTexture(pixels, 32, 32));
  glowTexture.needsUpdate = true;
  const particles = new T.InstancedMesh(
    own(new T.PlaneGeometry(1, 1)),
    own(
      new T.MeshBasicMaterial({
        map: glowTexture,
        transparent: true,
        blending: T.AdditiveBlending,
        depthWrite: false,
        toneMapped: false,
        side: T.DoubleSide,
      }),
    ),
    particleCapacity,
  );
  particles.name = 'Sky bounded explosion swarm';
  particles.instanceMatrix.setUsage(T.DynamicDrawUsage);
  particles.frustumCulled = false;
  particles.count = 0;
  scene.add(particles);
  const smoke = new T.InstancedMesh(
    particles.geometry,
    own(
      new T.MeshBasicMaterial({
        map: glowTexture,
        color: '#392f3a',
        transparent: true,
        opacity: 0.65,
        depthWrite: false,
        side: T.DoubleSide,
      }),
    ),
    96,
  );
  smoke.name = 'Sky lingering explosion smoke';
  smoke.instanceMatrix.setUsage(T.DynamicDrawUsage);
  smoke.frustumCulled = false;
  smoke.count = 0;
  scene.add(smoke);
  const particlePose = new T.Object3D(),
    particleColor = new T.Color();
  const hotColor = new T.Color('#fff2ba'),
    emberColor = new T.Color('#f86c25');
  const ionColor = new T.Color('#51dfff');
  let particleCount = 0,
    smokeCount = 0;
  function puff(x, y, z, size, angle) {
    if (smokeCount >= 96) return;
    particlePose.position.set(x, y, z);
    particlePose.rotation.set(0, 0, angle);
    particlePose.scale.setScalar(size);
    particlePose.updateMatrix();
    smoke.setMatrixAt(smokeCount++, particlePose.matrix);
  }
  function particle(x, y, z, size, stretch, angle, color, fade) {
    if (particleCount >= particleCapacity || fade <= 0) return;
    particlePose.position.set(x, y, z);
    particlePose.rotation.set(0, 0, angle);
    particlePose.scale.set(size, size * stretch, 1);
    particlePose.updateMatrix();
    particles.setMatrixAt(particleCount, particlePose.matrix);
    particleColor.copy(color).multiplyScalar(Math.min(1, fade) * 2.2);
    particles.setColorAt(particleCount++, particleColor);
  }
  function drawParticles(state) {
    particleCount = 0;
    smokeCount = 0;
    if (!reduced()) {
      // Reserve room for the larger foreground events even during dense kills.

      const boss = state.boss;
      if (boss && !boss.defeated && boss.maxHp > 0 && boss.hp / boss.maxHp < 0.66) {
        const severity = 1 - boss.hp / boss.maxHp;
        const vents = severity > 0.66 ? 3 : 1;
        for (let vent = 0; vent < vents; vent++) {
          const x = boss.x + (vent - (vents - 1) / 2) * 1.5;
          for (let i = 0; i < 8; i++) {
            const age = ((boss.age ?? state.time) + i * 0.1) % 0.8;
            particle(
              x + Math.sin(i * 2.4) * age * 0.5,
              boss.y + age * 2,
              boss.z + 2.5,
              0.2 * (1 - age),
              2.4,
              i,
              emberColor,
              0.8 - age,
            );
          }
          const age = ((boss.age ?? state.time) + vent * 0.3) % 1.2;
          puff(x, boss.y + age * 2, boss.z + 1, 0.6 + age * 1.4, vent);
        }
      }
      if (boss?.defeated && Number.isFinite(boss.deathAge)) {
        const duration = boss.deathDuration ?? 3.2;
        const organic = boss.form === 'leviathan';
        const stone = boss.profile === 'stoneOracle';
        const demise = boss.deathAge / duration;
        for (let i = 0; i < 20 && demise > 0.2 && demise < 1; i++) {
          const a = i * 2.399;
          const spread = (demise - 0.2) * 8;
          puff(
            boss.x + Math.cos(a) * spread,
            boss.y + Math.sin(a) * spread * 0.5 + demise * 2,
            boss.z + 1,
            (1 + demise * 5) * (1 - demise ** 4),
            a,
          );
        }
        for (let i = 0; i < 128; i++) {
          const delay = (i % 4) * duration * 0.085;
          const age = Math.max(0, boss.deathAge - delay);
          const phase = age / duration;
          if (age <= 0 || phase >= 1) continue;
          const angle = i * 2.39996323;
          const travel = age * (2.4 + (i % 11) * 0.55);
          particle(
            boss.x + Math.cos(angle) * travel,
            boss.y + Math.sin(angle) * travel - age * age * 1.5,
            boss.z + Math.sin(i * 1.31) * travel * 0.6,
            0.3 * (1 - phase * 0.7),
            2.3,
            angle - Math.PI / 2,
            stone ? hotColor : !organic && i % 3 ? ionColor : phase < 0.28 ? hotColor : emberColor,
            1 - phase,
          );
        }
      }
      if (state.pulseTime > 0) {
        const age = Math.max(0, 0.65 - state.pulseTime);
        for (let i = 0; i < 96; i++) {
          const angle = (i * Math.PI * 2) / 96;
          const radius = 0.5 + age * (14 + (i % 3) * 1.8);
          particle(
            state.x + Math.cos(angle) * radius,
            state.y + Math.sin(angle) * radius,
            -2 - age * (8 + (i % 5)),
            0.18,
            3.5,
            angle - Math.PI / 2,
            i % 3 ? ionColor : hotColor,
            Math.max(0, state.pulseTime / 0.65),
          );
        }
      }
    }
    smoke.count = smokeCount;
    smoke.visible = smokeCount > 0;
    smoke.instanceMatrix.needsUpdate = true;
    particles.count = particleCount;
    particles.visible = particleCount > 0;
    particles.instanceMatrix.needsUpdate = true;
    if (particles.instanceColor) particles.instanceColor.needsUpdate = true;
  }
  function createProjectile(item) {
    const model = new T.Group();
    model.userData.effectKind = item.kind;
    if (item.kind === 'burst') {
      model.visible = false; // The shared impact batches own all burst art.
    } else if (item.kind === 'threat') return dragonFire.create(item);
    else return projectileArt.create(item);
    return model;
  }
  const clearPulse = new T.Mesh(ring, additive('#9bffe2', 0.65));
  clearPulse.name = 'Sky wave-clear reward pulse';
  scene.add(clearPulse);
  function updateProjectile(model, item, state) {
    model.position.set(item.x, item.y, item.z);
    if (item.kind === 'burst') {
      model.visible = false;
    } else if (item.kind === 'bolt') {
      projectileArt.update(model, item);
    } else if (item.kind === 'threat') {
      dragonFire.update(model, item);
    }
  }
  const destruction = new T.Group();
  destruction.name = 'Guardian reactor collapse';
  scene.add(destruction);
  const collapseCore = new T.Mesh(sphere, additive('#ffc79b', 0.75));
  destruction.add(collapseCore);
  const collapseWave = new T.Mesh(ring, additive('#ff8d58', 0.65));
  destruction.add(collapseWave);
  const debris = new T.InstancedMesh(
    sphere,
    own(new T.MeshStandardMaterial({ color: '#594c40', roughness: 0.85, metalness: 0.45 })),
    24,
  );
  destruction.add(debris);
  const debrisPose = new T.Object3D();
  function drawDestruction(state) {
    const boss = state.boss;
    const active = boss?.defeated && Number.isFinite(boss.deathAge);
    destruction.visible = !!active;
    if (!active) return;
    const age = Math.max(0, boss.deathAge),
      duration = boss.deathDuration ?? 3.2;
    const progress = Math.min(1, age / duration);
    destruction.position.set(boss.x, boss.y, boss.z);
    // One contained reactor contraction, then a readable expanding pressure ring.
    // Reduced motion retains a quiet fading core without expanding debris.
    const quiet = reduced();
    collapseCore.scale.setScalar(
      quiet ? 0.8 : progress < 0.28 ? 1.6 * (1 - progress / 0.35) : 0.3 + (progress - 0.28) * 6,
    );
    collapseCore.material.opacity = (quiet ? 0.35 : 0.8) * (1 - progress);
    collapseWave.visible = !quiet && progress > 0.25;
    collapseWave.scale.setScalar(0.5 + Math.max(0, progress - 0.25) * 17);
    collapseWave.material.opacity = 0.65 * (1 - progress);
    debris.visible = !quiet;
    for (let i = 0; i < 24; i++) {
      const angle = i * 2.39996323,
        speed = 3 + (i % 5) * 0.8;
      const travel = Math.max(0, age - duration * 0.2);
      debrisPose.position.set(
        Math.cos(angle) * travel * speed,
        Math.sin(angle) * travel * speed - travel * travel * 2,
        Math.sin(i * 3.1) * travel * speed * 0.45,
      );
      debrisPose.rotation.set(angle + travel * 2, angle * 0.3 + travel, travel * 3);
      debrisPose.scale.set(0.06 + (i % 3) * 0.045, 0.12 + (i % 4) * 0.05, 0.04);
      debrisPose.scale.multiplyScalar(1 - progress * 0.75);
      debrisPose.updateMatrix();
      debris.setMatrixAt(i, debrisPose.matrix);
    }
    debris.instanceMatrix.needsUpdate = true;
  }
  let anchorRider = null,
    muzzleBindKey = '';
  const defaultMuzzleBind = [0.61, 0.75, 0.13];
  let anchors = [];
  const worldPoint = new T.Vector3();
  const worldRotation = new T.Quaternion();
  const parentRotation = new T.Quaternion();
  const nozzleGimbal = new T.Quaternion().setFromAxisAngle(new T.Vector3(1, 0, 0), 0.3);
  function bindAnchors(rider) {
    anchorRider = rider;
    anchors = [];
    let skeleton;
    rider.traverse((node) => {
      if (!skeleton && node.isSkinnedMesh) skeleton = node.skeleton;
    });
    if (!skeleton) return;
    const bind = (object, boneName, point, orient) => {
      const index = skeleton.bones.findIndex((bone) => bone.name === boneName);
      if (index < 0) return;
      anchors.push({
        object,
        bone: skeleton.bones[index],
        point: new T.Vector3(...point).applyMatrix4(skeleton.boneInverses[index]),
        orient,
        orientation: rider.userData.skyEquipmentTransforms?.[boneName]
          ? new T.Quaternion().setFromRotationMatrix(
              skeleton.boneInverses[index]
                .clone()
                .multiply(
                  new T.Matrix4().fromArray(rider.userData.skyEquipmentTransforms[boneName]),
                ),
            )
          : new T.Quaternion(),
      });
    };
    // Original GLB bind space faces +Z; bone world matrices include the model's
    // facing correction, animation and rider wrapper without duplicating them.
    bind(
      plumes.children[0],
      'spine',
      rider.userData.skyBoosterBinds?.[0] ?? [-0.27, 1.48, -0.31],
      true,
    );
    bind(
      plumes.children[1],
      'spine',
      rider.userData.skyBoosterBinds?.[1] ?? [0.27, 1.48, -0.31],
      true,
    );
    const muzzleBind = rider.userData.skyWeaponMuzzleBind ?? defaultMuzzleBind;
    muzzleBindKey = muzzleBind.join(',');
    bind(muzzle, 'forearmR', muzzleBind, false);
    bind(
      dualMuzzle,
      'forearmR',
      rider.userData.skyDualMuzzleBind ?? [0.81, muzzleBind[1], 0.13],
      false,
    );
  }
  function updateAnchors(rider) {
    const key = (rider.userData.skyWeaponMuzzleBind ?? defaultMuzzleBind).join(',');
    if (anchorRider !== rider || key !== muzzleBindKey) bindAnchors(rider);
    if (!anchors.length) return;
    rider.updateMatrixWorld(true);
    playerEffects.updateMatrixWorld(true);
    playerEffects.getWorldQuaternion(parentRotation).invert();
    for (const anchor of anchors) {
      worldPoint.copy(anchor.point).applyMatrix4(anchor.bone.matrixWorld);
      anchor.object.position.copy(playerEffects.worldToLocal(worldPoint));
      if (anchor.orient) {
        anchor.bone.getWorldQuaternion(worldRotation);
        anchor.object.quaternion
          .copy(parentRotation)
          .multiply(worldRotation)
          .multiply(anchor.orientation)
          .multiply(nozzleGimbal);
      }
    }
  }
  function draw(state, rider, camera, aim) {
    dragonFire.draw(state, camera);
    impacts.draw(state, camera);
    scorePopups.draw(state, camera, aim);
    drawDestruction(state);
    drawParticles(state);
    impactLight.intensity = 0;
    if (!reduced()) {
      const flash = state.bursts?.findLast(
        (item) => item.effect === 'kill' && (item.duration ?? 0.6) - item.life < 0.16,
      );
      if (flash) {
        const age = Math.max(0, (flash.duration ?? 0.6) - flash.life);
        impactLight.position.set(flash.x, flash.y, flash.z + 1);
        impactLight.intensity = 16 * (1 - age / 0.16) ** 2;
      }
    }
    const boss = state.boss;
    const hazard = boss ? skyGuardianWake(boss.profile) : null;
    const wakeAge = (boss?.age ?? 0) - (hazard?.impactAge ?? 0);
    wake.visible = !!boss && !boss.defeated && wakeAge > -1.2 && wakeAge < 0.3;
    if (wake.visible) {
      const travel = Math.max(0, Math.min(1, (wakeAge + 0.4) / 0.4));
      wake.position.set(hazard.x, hazard.y, reduced() ? hazard.z : -9 + travel * 10);
      wake.scale.setScalar(hazard.radius);
      wake.material.opacity = reduced()
        ? 0.28
        : wakeAge > 0
          ? 0.7 * (1 - wakeAge / 0.3)
          : 0.25 + travel * 0.45;
    }
    const clear = state.lastSquadClear;
    const clearAge = clear ? state.time - clear.time : Infinity;
    clearPulse.visible = !reduced() && clearAge >= 0 && clearAge < 0.9;
    if (clearPulse.visible) {
      clearPulse.position.set(clear.x, clear.y, clear.z);
      clearPulse.scale.setScalar(0.5 + clearAge * 4);
      clearPulse.material.opacity = (1 - clearAge / 0.9) * 0.65;
      clearPulse.material.color.set(clear.perfect ? '#ffdf83' : '#9bffe2');
    }
    playerEffects.position.copy(rider.position);
    playerEffects.quaternion.copy(rider.quaternion);
    playerEffects.scale.copy(rider.scale).multiplyScalar(SKY_WORLD_ASSETS.pilot.modelScale);
    playerEffects.visible = rider.visible;
    updateAnchors(rider);
    const flying = state.locomotion === 'fly' || (!state.locomotion && state.y > 1.55);
    const propulsion = T.MathUtils.clamp(state.propulsionScale ?? 1, 0, 1);
    plumes.visible =
      flying && propulsion > 0.01 && ['playing', 'paused', 'cinematic'].includes(state.status);
    const travel = rider.userData.skyTravel;
    const turn = reduced() ? 0 : (travel?.turn ?? 0);
    const climb = reduced() ? 0 : (travel?.climb ?? 0);
    const equipment = skyEffectiveEquipment(state);
    const booster = state.upgrades?.booster ?? 0;
    const surge = state.overdriveTime > 0 ? 0.22 : 0;
    for (const [index, plume] of plumes.children.entries()) {
      const side = index ? 1 : -1;
      plume.scale.y =
        (1 + booster * 0.13 + surge + Math.max(0, climb) * 0.18 + side * turn * 0.045) * propulsion;
      plume.scale.x = plume.scale.z =
        (1 + booster * 0.055 + surge * 0.3 + Math.max(0, climb) * 0.06) * Math.sqrt(propulsion);
      if (camera) {
        // Only cosmetic exhaust retracts near the lens; the physical nozzle and
        // propulsion state stay fixed. Fragment depth fading softens crossings.
        plume.getWorldPosition(worldPoint).applyMatrix4(camera.matrixWorldInverse);
        const clearance = T.MathUtils.smoothstep(-worldPoint.z, 0.75, 2.2);
        plume.scale.y *= 0.3 + 0.7 * clearance;
        plume.scale.x *= 0.6 + 0.4 * clearance;
        plume.scale.z = plume.scale.x;
      }
    }
    plumeMaterial.uniforms.time.value = state.time;
    plumeMaterial.uniforms.moving.value = reduced() ? 0 : 1;
    const charged =
      state.bossSupercharge?.status === 'active' && state.bossSupercharge.remaining > 0;
    plumeMaterial.uniforms.charge.value = charged ? 1 : 0;
    const aegis = skyRewardActive(state, 'shield');
    shield.visible = (state.grace > 0 || aegis) && state.status === 'playing';
    shield.material.opacity = reduced()
      ? 0.1
      : Math.min(0.28, Math.max(state.grace ?? 0, aegis ? 2 : 0) * 0.13);
    shield.material.color.set(
      state.upgrades?.shield === 2
        ? '#bc89ff'
        : state.upgrades?.shield === 1
          ? '#6aff9b'
          : '#73dfff',
    );
    muzzle.material = charged ? supercharged : (weaponCores[state.upgrades?.weapon ?? 0] ?? ice);
    for (const [barrel, tracer] of [launchTracer, dualTracer].entries()) {
      const latest = state.shots?.findLast(
        (shot) => shot.source !== 'drone' && (shot.barrel ?? 0) === barrel,
      );
      const speed = latest
        ? Math.hypot(latest.vx ?? 0, latest.vy ?? 0, latest.vz ?? -(latest.speed ?? 120))
        : 1;
      const age = latest ? (latest.distance ?? 0) / Math.max(1, speed) : 1;
      tracer.visible =
        !reduced() &&
        state.status === 'playing' &&
        !state.traversal &&
        !!latest?.origin &&
        age < 0.035;
      if (!tracer.visible) continue;
      beamDirection
        .set(latest.vx ?? 0, latest.vy ?? 0, latest.vz ?? -(latest.speed ?? 120))
        .normalize();
      const length = Math.min(0.6, latest.distance ?? 0);
      // A short launch flash stays on the immutable fired ray. Connecting the
      // current moving hand to an old bolt made the weapon appear to cast a lasso.
      tracer.position
        .set(latest.origin.x, latest.origin.y, latest.origin.z)
        .addScaledVector(beamDirection, length * 0.5);
      tracer.scale.set(1, length, 1);
      tracer.quaternion.setFromUnitVectors(beamUp, beamDirection);
      tracer.material = weaponCores[latest.weaponLevel ?? 0] ?? ice;
    }
    const interval = skyCannonInterval(state, equipment);
    const firing = skyFlightCapabilities(state).weapons && state.shot > interval - 0.045;
    muzzle.visible = firing && !reduced();
    muzzle.scale.setScalar(0.035 + Math.max(0, state.shot - (interval - 0.045)) * 1.6);
    dualMuzzle.visible = muzzle.visible && equipment.shotCount === 2;
    dualMuzzle.material = muzzle.material;
    dualMuzzle.scale.copy(muzzle.scale);
    nova.visible = state.pulseTime > 0 && !reduced();
    nova.position.set(state.x, state.y, -2);
    const starburst = state.upgrades?.nova === 3;
    nova.children[2].visible = starburst;
    nova.scale.setScalar(1 + Math.max(0, 0.65 - state.pulseTime) * (starburst ? 29 : 25));
  }
  return {
    playerEffects,
    createProjectile,
    updateProjectile,
    warmup: () => dragonFire.warmup(),
    draw,
    dispose() {
      if (disposed) return;
      disposed = true;
      dragonFire.dispose();
      impacts.dispose();
      scorePopups.dispose();
      scene.remove(
        playerEffects,
        nova,
        destruction,
        particles,
        smoke,
        wake,
        launchTracer,
        dualTracer,
        impactLight,
        clearPulse,
      );
      smoke.dispose();
      particles.dispose();
      debris.dispose();
      for (const resource of owned) resource.dispose();
      owned.clear();
      anchors = [];
      anchorRider = null;
    },
  };
}
